import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight, ArrowDown, ArrowUpRight, Clock, HelpCircle,
    Target, Split,  Flame, 
    Zap,  Layers,  CheckCircle2, Check,
     Search, AlertTriangle,  ShieldCheck,
    Filter, TrendingDown, Timer, BarChart3, Plus,
    GripVertical, Route, GitBranch,
    ShoppingCart, UserPlus, BookOpen, Compass, Cookie,
    FileBarChart, ChevronRight, Percent, MousePointerClick, Video
} from 'lucide-react';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';

const ServiceFunnel = () => {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // 활용 사례
    const useCases = [
        {
            emoji: <ShoppingCart className="w-5 h-5" />,
            title: '이커머스 구매 퍼널',
            type: '구매 전환',
            problem: '상품 상세 → 장바구니 단계에서 전환율이 12%로 극도로 낮음',
            solution: '퍼널 데이터 기반으로 "바로 구매" 버튼 추가, 장바구니 단계 간소화',
            result: '구매 전환율 2.8배 상승',
            color: 'violet',
            glow: 'shadow-violet-500/20'
        },
        {
            emoji: <UserPlus className="w-5 h-5" />,
            title: '회원가입 퍼널',
            type: '가입 전환',
            problem: '이메일 인증 단계에서 사용자 58%가 이탈 — 인증 메일 미확인',
            solution: '인증 대기 화면에 재발송 버튼과 스팸함 안내 문구 추가',
            result: '가입 완료율 35% 증가',
            color: 'purple',
            glow: 'shadow-purple-500/20'
        },
        {
            emoji: <BookOpen className="w-5 h-5" />,
            title: '콘텐츠 소비 퍼널',
            type: '콘텐츠 전환',
            problem: '블로그 → CTA 클릭 → 무료 체험까지 도달하는 비율이 0.8%에 불과',
            solution: '콘텐츠 중간에 인라인 CTA 삽입, 하단 CTA를 상단 30%로 이동',
            result: 'CTA 클릭률 4.2배 증가',
            color: 'blue',
            glow: 'shadow-blue-500/20'
        },
        {
            emoji: <Compass className="w-5 h-5" />,
            title: '온보딩 퍼널',
            type: '활성화 전환',
            problem: '가입 후 첫 프로젝트 생성까지 평균 4.2일 소요 — 7일 내 이탈률 70%',
            solution: '온보딩 가이드 추가, 첫 프로젝트 자동 생성으로 Time-to-Value 단축',
            result: '7일 리텐션 45% 개선',
            color: 'teal',
            glow: 'shadow-teal-500/20'
        }
    ];

    return (
        <div className="bg-white">
            {/* 플로팅 CTA 버튼 */}
            <div className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <Link to="/freestart">
                    <button className="flex items-center bg-gradient-to-r from-purple-600 to-violet-600 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group">
                        <span className="font-medium">무료로 시작하기</span>
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </button>
                </Link>
            </div>

            <Header />

            {/* ==========================================
          섹션 1: Hero Section
      ========================================== */}
            <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[#0B0F19]">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:40px_40px]"></div>
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[120px] pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-violet-600/10 rounded-full blur-[100px] pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">

                        {/* Left: 텍스트 */}
                        <div className="max-w-2xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-semibold mb-8">
                                <Filter className="w-4 h-4 fill-purple-500" />
                                <span>Funnel Analytics Platform</span>
                            </div>

                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                                어디서 고객을 <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-violet-500 to-pink-500">잃고 있는지 확인하세요</span>
                            </h1>

                            <p className="text-base sm:text-xl text-gray-400 mb-10 leading-relaxed max-w-lg">
                                전환율 3%라는 숫자는 '결과'일 뿐, '어디서' 97%를 잃었는지 말해주지 않습니다.
                                단계별 이탈 지점을 시각화하여 병목 구간을 즉시 발견하세요.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/freestart">
                                    <button className="px-8 py-4 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 text-white rounded-xl font-bold text-lg shadow-lg shadow-purple-900/20 hover:shadow-purple-500/30 transition-all hover:-translate-y-1">
                                        무료로 퍼널 분석 시작하기
                                    </button>
                                </Link>
                                <Link to="/service/heatmap">
                                    <button className="px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-xl font-medium text-lg transition-all backdrop-blur-sm flex items-center justify-center gap-2 group">
                                        히트맵 분석 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                            </div>

                            <div className="mt-8 flex items-center gap-4 text-sm text-gray-500">
                                <div className="flex -space-x-2">
                                    {[1, 2, 3, 4].map(i => (
                                        <div key={i} className="w-8 h-8 rounded-full bg-gray-800 border-2 border-[#0B0F19] flex items-center justify-center text-xs font-bold text-gray-400">
                                            U{i}
                                        </div>
                                    ))}
                                </div>
                                <p>단계별 이탈 원인을 한눈에 파악하는 정밀 분석</p>
                            </div>
                        </div>

                        {/* Right: 퍼널 차트 3D 비주얼 */}
                        <div className="relative perspective-1000 hidden lg:block">
                            <div className="relative transform rotate-y-[-12deg] rotate-x-[5deg] transition-transform duration-700 hover:rotate-y-[-5deg] hover:rotate-x-[2deg]">
                                <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-2xl shadow-2xl overflow-hidden min-h-[500px] relative">

                                    {/* Browser Header */}
                                    <div className="bg-[#0F131C] px-4 py-3 flex items-center gap-2 border-b border-gray-800">
                                        <div className="flex gap-1.5">
                                            <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                                            <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                                            <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                                        </div>
                                        <div className="ml-4 bg-[#1A1F2E] px-3 py-1 rounded text-[10px] text-gray-500 font-mono flex-1 text-center">
                                            switchup.com/analytics/funnel
                                        </div>
                                    </div>

                                    {/* 퍼널 차트 시각화 */}
                                    <div className="p-8">
                                        {/* 퍼널 헤더 */}
                                        <div className="flex justify-between items-center mb-6">
                                            <div className="text-sm font-bold text-gray-300">구매 전환 퍼널</div>
                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                                실시간
                                            </div>
                                        </div>

                                        {/* 퍼널 바 차트 */}
                                        <div className="space-y-3">
                                            {[
                                                { label: '페이지 방문', value: 10000, width: '100%', color: 'from-purple-500 to-purple-600', percent: '100%' },
                                                { label: '상품 조회', value: 6200, width: '62%', color: 'from-purple-500 to-violet-500', percent: '62%' },
                                                { label: '장바구니 추가', value: 2480, width: '24.8%', color: 'from-violet-500 to-violet-600', percent: '40%' },
                                                { label: '결제 시작', value: 1240, width: '12.4%', color: 'from-violet-600 to-pink-500', percent: '50%' },
                                                { label: '구매 완료', value: 310, width: '3.1%', color: 'from-pink-500 to-pink-600', percent: '25%' },
                                            ].map((step, idx) => (
                                                <div key={idx} className="relative">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-[11px] text-gray-400 font-medium">{step.label}</span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[11px] font-bold text-white">{step.value.toLocaleString()}</span>
                                                            {idx > 0 && (
                                                                <span className="text-[10px] text-red-400 font-medium">▼ {step.percent}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="h-6 bg-gray-800 rounded-md overflow-hidden relative">
                                                        <div
                                                            className={`h-full bg-gradient-to-r ${step.color} rounded-md transition-all duration-1000 relative`}
                                                            style={{ width: step.width }}
                                                        >
                                                            {idx === 3 && (
                                                                <div className="absolute -right-1 top-0 bottom-0 w-1 bg-red-500 animate-pulse rounded"></div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* 하단 요약 */}
                                        <div className="mt-6 pt-4 border-t border-gray-800 flex justify-between items-center">
                                            <div className="text-xs text-gray-500">
                                                전체 전환율
                                            </div>
                                            <div className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-400">
                                                3.1%
                                            </div>
                                        </div>
                                    </div>

                                    {/* Floating Tooltip: 병목 구간 */}
                                    <div className="absolute top-[62%] right-28 bg-gray-900/90 backdrop-blur border border-red-500/30 text-white px-4 py-2 rounded-lg shadow-xl z-20 animate-bounce-slow">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                                            <span className="text-xs font-bold text-red-400 uppercase">병목 구간</span>
                                        </div>
                                        <div className="text-sm font-bold">
                                            결제 시작 → 구매 완료
                                        </div>
                                        <div className="text-xs text-gray-400 mt-0.5">75% 이탈 발생</div>
                                    </div>
                                </div>

                                <div className="absolute -bottom-10 left-10 right-10 h-20 bg-purple-500/20 blur-[60px] rounded-full transform scale-y-50"></div>
                            </div>
                        </div>

                    </div>
                </div>

                <style>{`
          .perspective-1000 { perspective: 1000px; }
          .rotate-y-\\[-12deg\\] { transform: rotateY(-12deg) rotateX(5deg); }
          @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
        `}</style>
            </section>

            {/* ==========================================
          섹션 2: Why Funnel? — 기존 분석 vs 퍼널 분석
      ========================================== */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                            Why Funnel Analysis?
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                            전환율만 보면 <br className="sm:hidden" />
                            <span className="text-purple-600">'어디서'</span>를 놓칩니다
                        </h2>
                        <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                            전체 전환율은 '결과'만 보여주지만, 퍼널 분석은 '과정'의 어디가 막혔는지 알려줍니다.
                        </p>
                    </div>

                    <div className="relative max-w-5xl mx-auto">
                        <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full items-center justify-center shadow-lg z-10 font-black text-gray-300 italic text-xl border-4 border-gray-50">
                            VS
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 md:gap-0">

                            {/* 기존 분석 */}
                            <div className="bg-white p-8 md:p-12 rounded-2xl md:rounded-r-none md:rounded-l-2xl border border-gray-200 shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-100 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                                <div className="relative z-10">
                                    <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 text-gray-400">
                                        <BarChart3 className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-500 mb-2">기존 데이터 분석</h3>
                                    <p className="text-gray-400 text-sm mb-8 font-medium">Just a Single Number</p>

                                    <ul className="space-y-6">
                                        {[
                                            { text: "전환율이 3%입니다", sub: "나머지 97%는 어디서 빠졌죠?" },
                                            { text: "이번 달 매출이 줄었습니다", sub: "어느 단계가 무너진 건가요?" },
                                            { text: "가입자 수가 적습니다", sub: "어떤 단계에서 포기하나요?" }
                                        ].map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-4 opacity-70 group-hover:opacity-100 transition-opacity">
                                                <div className="mt-1 min-w-[24px]">
                                                    <HelpCircle className="w-6 h-6 text-gray-300" />
                                                </div>
                                                <div>
                                                    <div className="text-gray-600 font-medium text-lg">"{item.text}"</div>
                                                    <div className="text-gray-400 text-xs mt-0.5">{item.sub}</div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* 퍼널 분석 */}
                            <div className="bg-[#1A1F2E] p-8 md:p-12 rounded-2xl md:rounded-l-none md:rounded-r-2xl border border-gray-700 shadow-2xl relative overflow-hidden transform md:scale-105 md:-my-4 z-0 ring-1 ring-purple-500/20">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-500/20 to-violet-500/20 rounded-full blur-3xl opacity-60 -mr-20 -mt-20 animate-pulse"></div>
                                <div className="relative z-10">
                                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center mb-6 text-white shadow-lg shadow-purple-500/30">
                                        <Filter className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">퍼널 분석</h3>
                                    <p className="text-purple-400 text-sm mb-8 font-bold tracking-wide">Step-by-Step Visibility</p>

                                    <ul className="space-y-6">
                                        {[
                                            { text: "장바구니→결제에서 62% 이탈", sub: "결제 UX 개선이 최우선 과제" },
                                            { text: "인증 단계에서 평균 4.2일 소요", sub: "인증 플로우 간소화 필요" },
                                            { text: "3단계 이후 전환율 급감", sub: "폼 필드 줄이기로 해결 가능" }
                                        ].map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-4">
                                                <div className="mt-1 min-w-[24px]">
                                                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                                                        <Check className="w-4 h-4 text-green-400 stroke-[3]" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-white font-bold text-lg">{item.text}</div>
                                                    <div className="text-gray-400 text-xs mt-0.5 font-medium">{item.sub}</div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="mt-10 pt-8 border-t border-gray-700">
                                        <div className="flex items-center gap-2 text-purple-400 font-bold">
                                            <span>병목 구간 발견 속도 10배 단축</span>
                                            <ArrowRight className="w-5 h-5" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 3: 핵심 지표 4가지 카드
      ========================================== */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                            4가지 핵심 지표로 완벽한 분석
                        </h2>
                        <p className="text-xl text-gray-600">
                            퍼널의 모든 단계를 숫자와 시각화로 <br className="sm:hidden" />놓치지 않습니다
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">

                        {/* 1. 도달률 — 줄어드는 바 시각화 */}
                        <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-purple-200 transition-all duration-300">
                            <div className="h-36 bg-gray-50 rounded-xl mb-6 relative overflow-hidden flex flex-col items-center justify-center gap-1.5 p-4 border border-gray-100 group-hover:bg-purple-50/30 transition-colors">
                                {[
                                    { w: 'w-full', opacity: 'opacity-90', label: '100%' },
                                    { w: 'w-[62%]', opacity: 'opacity-75', label: '62%' },
                                    { w: 'w-[25%]', opacity: 'opacity-60', label: '25%' },
                                    { w: 'w-[12%]', opacity: 'opacity-50', label: '12%' },
                                    { w: 'w-[3%]', opacity: 'opacity-40', label: '3.1%' },
                                ].map((bar, i) => (
                                    <div key={i} className="w-full flex items-center gap-2">
                                        <div className={`h-4 ${bar.w} bg-purple-500 ${bar.opacity} rounded-r-sm transition-all duration-500 group-hover:bg-purple-600`}></div>
                                        <span className="text-[9px] text-gray-400 font-mono font-medium whitespace-nowrap">{bar.label}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-purple-100 text-purple-600 rounded-lg">
                                    <Target className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">도달률</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                                전체 방문자 중 해당 단계까지 도달한 비율을 추적합니다.
                            </p>
                            <ul className="space-y-1.5">
                                {['첫 단계 대비 최종 전환율', '단계별 누적 도달 비율'].map((feat, i) => (
                                    <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                                        <CheckCircle2 className="w-3 h-3 text-purple-500 mr-2" /> {feat}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* 2. 단계 전환율 — 스텝 게이지 시각화 */}
                        <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-violet-200 transition-all duration-300">
                            <div className="h-36 bg-gray-50 rounded-xl mb-6 relative overflow-hidden flex items-end justify-center gap-3 px-4 pb-4 pt-6 border border-gray-100 group-hover:bg-violet-50/30 transition-colors">
                                {[
                                    { percent: 62, label: '62%', color: 'bg-violet-400' },
                                    { percent: 40, label: '40%', color: 'bg-violet-500' },
                                    { percent: 50, label: '50%', color: 'bg-violet-500' },
                                    { percent: 25, label: '25%', color: 'bg-violet-600' },
                                ].map((step, i) => (
                                    <div key={i} className="flex flex-col items-center gap-1 flex-1">
                                        <span className="text-[9px] text-gray-400 font-mono font-bold">{step.label}</span>
                                        <div className="w-full bg-gray-200 rounded-t-sm relative" style={{ height: '80px' }}>
                                            <div
                                                className={`absolute bottom-0 w-full ${step.color} rounded-t-sm transition-all duration-500`}
                                                style={{ height: `${step.percent}%` }}
                                            ></div>
                                        </div>
                                        <span className="text-[8px] text-gray-400">{i + 1}→{i + 2}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-violet-100 text-violet-600 rounded-lg">
                                    <Percent className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">단계 전환율</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                                이전 단계 대비 다음 단계로 넘어간 비율을 측정합니다.
                            </p>
                            <ul className="space-y-1.5">
                                {['단계별 개별 전환율 확인', '가장 낮은 전환 구간 파악'].map((feat, i) => (
                                    <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                                        <CheckCircle2 className="w-3 h-3 text-violet-500 mr-2" /> {feat}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* 3. 이탈 수 — 빠져나가는 점 시각화 */}
                        <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-red-200 transition-all duration-300">
                            <div className="h-36 bg-gray-50 rounded-xl mb-6 relative overflow-hidden flex items-center justify-center border border-gray-100 group-hover:bg-red-50/30 transition-colors">
                                {/* 퍼널 흐름 */}
                                <div className="flex items-center gap-1">
                                    <div className="flex flex-col items-center gap-0.5">
                                        {[1, 2, 3, 4, 5].map(i => (
                                            <div key={i} className="w-2.5 h-2.5 rounded-full bg-purple-400 opacity-80"></div>
                                        ))}
                                    </div>
                                    <ArrowRight className="w-3 h-3 text-gray-300" />
                                    <div className="flex flex-col items-center gap-0.5">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="w-2.5 h-2.5 rounded-full bg-purple-500 opacity-80"></div>
                                        ))}
                                        {/* 이탈 표시 */}
                                        <div className="relative">
                                            <div className="w-2.5 h-2.5 rounded-full bg-red-400 opacity-50 animate-ping absolute"></div>
                                            <div className="w-2.5 h-2.5 rounded-full bg-red-400 opacity-60"></div>
                                        </div>
                                        <div className="w-2.5 h-2.5 rounded-full bg-red-300 opacity-30"></div>
                                    </div>
                                    <ArrowRight className="w-3 h-3 text-gray-300" />
                                    <div className="flex flex-col items-center gap-0.5">
                                        <div className="w-2.5 h-2.5 rounded-full bg-purple-600 opacity-80"></div>
                                        <div className="relative">
                                            <div className="w-2.5 h-2.5 rounded-full bg-red-400 opacity-50 animate-ping absolute" style={{ animationDelay: '0.3s' }}></div>
                                            <div className="w-2.5 h-2.5 rounded-full bg-red-400 opacity-60"></div>
                                        </div>
                                        <div className="w-2.5 h-2.5 rounded-full bg-red-300 opacity-30"></div>
                                    </div>
                                </div>
                                {/* 이탈 화살표 */}
                                <div className="absolute bottom-3 right-4 flex items-center gap-1 text-red-400">
                                    <TrendingDown className="w-3 h-3" />
                                    <span className="text-[9px] font-bold font-mono">-3,800</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-red-100 text-red-600 rounded-lg">
                                    <TrendingDown className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">이탈 수</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                                각 단계에서 다음으로 넘어가지 못한 이탈 인원을 표시합니다.
                            </p>
                            <ul className="space-y-1.5">
                                {['이탈 인원 수치 표시', '전체 대비 이탈 비율 확인'].map((feat, i) => (
                                    <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                                        <CheckCircle2 className="w-3 h-3 text-red-500 mr-2" /> {feat}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* 4. 평균 전환 시간 — 타이머 시각화 */}
                        <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-blue-200 transition-all duration-300">
                            <div className="h-36 bg-gray-900 rounded-xl mb-6 relative overflow-hidden flex flex-col items-center justify-center p-4 border border-gray-100 group-hover:border-blue-200 transition-colors">
                                {/* 타이머 디스플레이 */}
                                <div className="text-3xl font-bold font-mono text-white mb-3 tracking-wider">
                                    <span className="text-blue-400">02</span>
                                    <span className="text-gray-500 animate-pulse">:</span>
                                    <span className="text-blue-300">30</span>
                                </div>
                                {/* 단계 표시 */}
                                <div className="flex items-center gap-2">
                                    <div className="px-2 py-0.5 bg-white/10 rounded text-[9px] text-gray-400 font-medium">장바구니</div>
                                    <ArrowRight className="w-3 h-3 text-gray-600" />
                                    <div className="px-2 py-0.5 bg-blue-500/20 rounded text-[9px] text-blue-300 font-medium">결제</div>
                                </div>
                                {/* 프로그레스 바 */}
                                <div className="absolute bottom-3 left-4 right-4 h-1 bg-gray-800 rounded-full overflow-hidden">
                                    <div className="h-full w-2/3 bg-blue-500 rounded-full animate-pulse"></div>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                    <Timer className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">평균 전환 시간</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                                단계 간 이동에 걸리는 평균 시간을 분석합니다.
                            </p>
                            <ul className="space-y-1.5">
                                {['단계 간 평균 소요 시간', 'UX 마찰 구간 탐지'].map((feat, i) => (
                                    <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                                        <CheckCircle2 className="w-3 h-3 text-blue-500 mr-2" /> {feat}
                                    </li>
                                ))}
                            </ul>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 4: 직관적인 퍼널 생성
      ========================================== */}
            <section className="py-24 bg-gray-50 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center gap-16">

                        {/* Left: 설명 */}
                        <div className="flex-1 max-w-xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-6">
                                <Plus className="w-4 h-4" />
                                <span>Funnel Builder</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                클릭 몇 번으로<br />
                                <span className="text-purple-600">퍼널을 완성하세요</span>
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                복잡한 설정 없이, 추적할 페이지 URL이나 이벤트 조건만 입력하면
                                자동으로 퍼널이 구성됩니다. 단계를 자유롭게 추가하고 순서를 변경하세요.
                            </p>

                            <div className="space-y-6 mb-8">
                                {[
                                    { title: "URL 기반 단계 설정", desc: "각 단계를 페이지 URL로 간단하게 정의합니다. 정규식 패턴도 지원합니다.", icon: <Route className="w-5 h-5 text-purple-600" /> },
                                    { title: "이벤트 기반 단계 설정", desc: "버튼 클릭, 폼 제출 등 특정 행동을 단계로 정의할 수 있습니다.", icon: <MousePointerClick className="w-5 h-5 text-purple-600" /> },
                                    { title: "자유로운 단계 편집", desc: "단계 추가, 삭제, 순서 변경이 자유롭습니다.", icon: <GripVertical className="w-5 h-5 text-purple-600" /> }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="mt-1 w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="p-4 bg-white rounded-xl border border-purple-100 shadow-sm">
                                <h4 className="font-semibold text-gray-900 mb-2 text-sm">설정 팁:</h4>
                                <ul className="space-y-2">
                                    {["핵심 전환 경로부터 먼저 설정", "3~7단계가 분석에 가장 적합", "이벤트와 URL을 조합하여 정밀 추적"].map((text, idx) => (
                                        <li key={idx} className="flex items-center text-sm text-gray-600">
                                            <CheckCircle2 className="w-4 h-4 text-purple-500 mr-2" />
                                            {text}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Right: 퍼널 생성 UI 목업 */}
                        <div className="flex-1 w-full relative">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden relative">
                                <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex justify-between items-center">
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">퍼널 생성</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-purple-400">새 퍼널</span>
                                    </div>
                                </div>

                                <div className="p-6 space-y-3">
                                    {/* 퍼널 이름 입력 */}
                                    <div className="mb-4">
                                        <label className="text-xs text-gray-500 font-medium block mb-1.5">퍼널 이름</label>
                                        <div className="h-10 bg-gray-50 border border-gray-200 rounded-lg px-3 flex items-center text-sm text-gray-700 font-medium">
                                            구매 전환 퍼널
                                        </div>
                                    </div>

                                    {/* 단계 리스트 */}
                                    <div className="space-y-2">
                                        {[
                                            { step: 1, name: '메인 페이지 방문', url: '/home', active: false },
                                            { step: 2, name: '상품 상세 조회', url: '/product/*', active: false },
                                            { step: 3, name: '장바구니 추가', url: '/cart', active: true },
                                            { step: 4, name: '결제 완료', url: '/checkout/complete', active: false },
                                        ].map((item) => (
                                            <div key={item.step} className={`flex items-center gap-3 p-3 rounded-xl border ${item.active ? 'border-purple-300 bg-purple-50/50 ring-1 ring-purple-200' : 'border-gray-200 bg-gray-50/50'} group/step hover:border-purple-200 transition-colors`}>
                                                <div className="text-gray-300 cursor-grab">
                                                    <GripVertical className="w-4 h-4" />
                                                </div>
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${item.active ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                                    {item.step}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-gray-800 truncate">{item.name}</div>
                                                    <div className="text-xs text-gray-400 font-mono truncate">{item.url}</div>
                                                </div>
                                                <ChevronRight className="w-4 h-4 text-gray-300" />
                                            </div>
                                        ))}
                                    </div>

                                    {/* 단계 추가 버튼 */}
                                    <button className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 font-medium flex items-center justify-center gap-2 hover:border-purple-300 hover:text-purple-500 transition-colors">
                                        <Plus className="w-4 h-4" />
                                        단계 추가
                                    </button>

                                    {/* 저장 버튼 */}
                                    <button className="w-full py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl text-sm font-bold mt-2">
                                        퍼널 저장하기
                                    </button>
                                </div>
                            </div>

                            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-purple-100 rounded-full blur-3xl -z-10"></div>
                            <div className="absolute -top-6 -left-6 w-32 h-32 bg-violet-100 rounded-full blur-3xl -z-10"></div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 5: 이탈 구간 즉시 발견
      ========================================== */}
            <section className="py-24 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row-reverse items-center gap-16">

                        {/* Right: 설명 (반전) */}
                        <div className="flex-1 max-w-xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-6">
                                <TrendingDown className="w-4 h-4" />
                                <span>Drop-off Detection</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                가장 많이 빠지는<br />
                                <span className="text-violet-600">병목 구간</span>을 한눈에
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                퍼널 리포트는 단계별 전환율과 이탈 수를 시각적으로 보여줍니다.
                                어느 단계에서 고객이 가장 많이 이탈하는지 즉시 파악하고,
                                가장 임팩트가 큰 개선 포인트를 찾으세요.
                            </p>

                            <div className="space-y-6 mb-8">
                                {[
                                    { title: "단계별 이탈 수 즉시 확인", desc: "각 단계에서 다음 단계로 넘어가지 못한 이탈 인원이 항상 표시됩니다.", icon: <TrendingDown className="w-5 h-5 text-violet-600" /> },
                                    { title: "단계별 세부 지표", desc: "도달률, 전환율, 이탈 수, 전환 시간을 단계마다 확인합니다.", icon: <BarChart3 className="w-5 h-5 text-violet-600" /> }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="mt-1 w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Left: 퍼널 리포트 차트 비주얼 */}
                        <div className="flex-1 w-full">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                                <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex justify-between items-center">
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">퍼널 리포트</span>
                                </div>

                                <div className="p-6">
                                    {/* SVG 퍼널 차트 */}
                                    <svg viewBox="0 0 500 280" className="w-full">
                                        {/* 배경 그리드 */}
                                        {[0, 1, 2, 3, 4].map(i => (
                                            <line key={i} x1={90 + i * 100} y1="20" x2={90 + i * 100} y2="230" stroke="#f3f4f6" strokeWidth="1" />
                                        ))}

                                        {/* 바 차트 */}
                                        {[
                                            { label: '방문', value: 10000, height: 200, y: 25, color: '#8b5cf6', sub: '100%' },
                                            { label: '조회', value: 6200, height: 124, y: 101, color: '#7c3aed', sub: '62%' },
                                            { label: '장바구니', value: 2480, height: 49.6, y: 175.4, color: '#9333ea', sub: '24.8%' },
                                            { label: '결제', value: 1240, height: 24.8, y: 200.2, color: '#c026d3', sub: '12.4%' },
                                            { label: '완료', value: 310, height: 6.2, y: 218.8, color: '#db2777', sub: '3.1%' },
                                        ].map((bar, idx) => (
                                            <g key={idx}>
                                                <rect
                                                    x={50 + idx * 95}
                                                    y={bar.y}
                                                    width="70"
                                                    height={bar.height}
                                                    fill={bar.color}
                                                    rx="4"
                                                    opacity="0.85"
                                                />
                                                {/* 바 위 수치 */}
                                                <text x={85 + idx * 95} y={bar.y - 8} textAnchor="middle" fill="#374151" fontSize="11" fontWeight="600">
                                                    {bar.value.toLocaleString()}
                                                </text>
                                                {/* 하단 레이블 */}
                                                <text x={85 + idx * 95} y="250" textAnchor="middle" fill="#9ca3af" fontSize="10" fontWeight="500">
                                                    {bar.label}
                                                </text>
                                                {/* 도달률 */}
                                                <text x={85 + idx * 95} y="265" textAnchor="middle" fill={bar.color} fontSize="10" fontWeight="700">
                                                    {bar.sub}
                                                </text>

                                                {/* 드롭오프 커넥터 */}
                                                {idx < 4 && (
                                                    <g>
                                                        <line
                                                            x1={120 + idx * 95}
                                                            y1={bar.y + bar.height / 2}
                                                            x2={145 + idx * 95}
                                                            y2={bar.y + bar.height / 2}
                                                            stroke="#ef4444"
                                                            strokeWidth="1"
                                                            strokeDasharray="3 2"
                                                            opacity="0.5"
                                                        />
                                                    </g>
                                                )}
                                            </g>
                                        ))}

                                    </svg>

                                    {/* 하단 요약 바 */}
                                    <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4 justify-center text-xs">
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-2.5 h-2.5 rounded-full bg-purple-500"></div>
                                            <span className="text-gray-500">도달률 기준</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                                            <span className="text-gray-500">단계별 이탈 표시</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 6: 전환 시간 분석
      ========================================== */}
            <section className="py-24 bg-gray-50 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center gap-16">

                        {/* Left: 설명 */}
                        <div className="flex-1 max-w-xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                                <Timer className="w-4 h-4" />
                                <span>Transition Time Analysis</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                단계 사이의<br />
                                <span className="text-blue-600">숨겨진 시간</span>을 측정하세요
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                전환율만큼 중요한 것이 전환 시간입니다.
                                사용자가 다음 단계로 넘어가는 데 걸리는 시간이 길다면,
                                UX에 마찰(Friction)이 있다는 신호입니다.
                            </p>

                            <div className="space-y-6 mb-8">
                                {[
                                    { title: "평균 전환 시간 측정", desc: "각 단계 간 이동에 소요되는 평균 시간을 자동으로 계산합니다.", icon: <Clock className="w-5 h-5 text-blue-600" /> },
                                    { title: "UX 마찰 구간 발견", desc: "시간이 비정상적으로 오래 걸리는 구간을 빠르게 찾아냅니다.", icon: <AlertTriangle className="w-5 h-5 text-blue-600" /> }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="mt-1 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right: 타임라인 시각화 */}
                        <div className="flex-1 w-full">
                            <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-800 p-6 relative overflow-hidden group">
                                <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:24px_24px] opacity-20"></div>
                                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>

                                <div className="relative z-10 space-y-6">
                                    {[
                                        { from: '메인 페이지', to: '상품 조회', time: '45초', timeColor: 'text-green-400', barWidth: 'w-1/6', barColor: 'bg-green-500' },
                                        { from: '상품 조회', to: '장바구니', time: '2분 30초', timeColor: 'text-yellow-400', barWidth: 'w-2/5', barColor: 'bg-yellow-500' },
                                        { from: '장바구니', to: '결제 시작', time: '4분 15초', timeColor: 'text-red-400', barWidth: 'w-3/5', barColor: 'bg-red-500', highlight: true },
                                        { from: '결제 시작', to: '구매 완료', time: '1분 20초', timeColor: 'text-green-400', barWidth: 'w-1/4', barColor: 'bg-green-500' },
                                    ].map((item, idx) => (
                                        <div key={idx} className={`relative p-4 rounded-xl ${item.highlight ? 'bg-red-500/10 border border-red-500/30' : 'bg-slate-800/50 border border-slate-700'}`}>
                                            {item.highlight && (
                                                <div className="absolute -top-2 right-3 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                                    주의 구간
                                                </div>
                                            )}

                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <span className="text-slate-400 font-medium">{item.from}</span>
                                                    <ArrowRight className="w-3 h-3 text-slate-600" />
                                                    <span className="text-white font-bold">{item.to}</span>
                                                </div>
                                                <div className={`text-sm font-bold ${item.timeColor} font-mono`}>
                                                    {item.time}
                                                </div>
                                            </div>

                                            {/* 타임 바 */}
                                            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                                                <div className={`h-full ${item.barColor} rounded-full ${item.barWidth} transition-all duration-1000`} style={{ opacity: 0.7 }}></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full border border-white/10 flex items-center gap-2 text-[10px] text-white shadow-lg pointer-events-none">
                                    <Timer className="w-3 h-3" />
                                    <span>평균 전환 시간</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 7: 활용 사례
      ========================================== */}
            <section className="py-24 bg-slate-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4">
                            <Search className="w-3 h-3" />
                            <span>Use Cases</span>
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                            퍼널 분석으로 발견한 <br className="sm:hidden" />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-500">결정적 순간들</span>
                        </h2>
                        <p className="text-xl text-slate-400">
                            막연한 추측 대신, 데이터로 <br className="sm:hidden" />병목을 찾고 해결한 사례입니다.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                        {useCases.map((item, index) => (
                            <div
                                key={index}
                                className={`group relative bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:bg-slate-800 transition-all duration-300 hover:-translate-y-1 overflow-hidden`}
                            >
                                <div className={`absolute top-0 right-0 w-32 h-32 bg-${item.color}-500/10 rounded-full blur-3xl -mr-10 -mt-10 group-hover:opacity-100 transition-opacity`}></div>

                                <div className="flex justify-between items-start mb-6 relative z-10">
                                    <div className={`p-3 rounded-xl bg-slate-900 border border-slate-700 text-${item.color}-400 group-hover:text-white group-hover:bg-${item.color}-600 transition-colors shadow-lg ${item.glow}`}>
                                        {item.emoji}
                                    </div>
                                    <span className={`px-3 py-1 rounded-full bg-${item.color}-900/30 text-${item.color}-400 text-xs font-bold border border-${item.color}-500/20`}>
                                        {item.type}
                                    </span>
                                </div>

                                <h3 className="text-2xl font-bold text-white mb-6 relative z-10">
                                    {item.title}
                                </h3>

                                <div className="space-y-6 mb-8 relative z-10">
                                    <div className="flex gap-4">
                                        <div className="mt-1 min-w-[24px]">
                                            <AlertTriangle className="w-5 h-5 text-red-400" />
                                        </div>
                                        <div>
                                            <span className="text-xs font-bold text-red-400 uppercase tracking-wide block mb-1">발견된 문제</span>
                                            <p className="text-sm text-slate-300 leading-relaxed">{item.problem}</p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="mt-1 min-w-[24px]">
                                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                        </div>
                                        <div>
                                            <span className="text-xs font-bold text-emerald-400 uppercase tracking-wide block mb-1">데이터 기반 해결</span>
                                            <p className="text-sm text-slate-300 leading-relaxed">{item.solution}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="relative z-10 bg-slate-900 rounded-xl p-4 border border-slate-700 flex items-center justify-between group-hover:border-slate-600 transition-colors shadow-inner">
                                    <div>
                                        <div className="text-xs text-slate-500 mb-1 font-medium uppercase">Impact</div>
                                        <div className={`text-xl font-bold text-${item.color}-400`}>{item.result}</div>
                                    </div>
                                    <div className={`w-10 h-10 rounded-full bg-${item.color}-500/10 flex items-center justify-center text-${item.color}-400`}>
                                        <ArrowUpRight className="w-5 h-5" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 8: 주요 특징 벤토 그리드
      ========================================== */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            정확하고 가벼운 추적 엔진
                        </h2>
                        <p className="text-xl text-gray-600">
                            사이트 성능 저하 없이, 고객 여정의<br className="sm:hidden" /> 모든 단계를 놓치지 않습니다.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                        {/* 1. 쿠키 기반 추적 (Large Card) */}
                        <div className="md:col-span-2 bg-gray-50 rounded-2xl p-8 border border-gray-200 relative overflow-hidden group hover:border-purple-200 transition-colors">
                            <div className="absolute top-6 right-6 flex items-center gap-2 px-3 py-1 bg-white rounded-full border border-gray-200 shadow-sm">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                                </span>
                                <span className="text-xs font-bold text-gray-600">Tracking</span>
                            </div>

                            <div className="relative z-10">
                                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-6 text-purple-600">
                                    <Cookie className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">쿠키 기반 정확한 추적</h3>
                                <p className="text-gray-600 leading-relaxed max-w-md">
                                    30일간 유지되는 쿠키로 방문자를 정확히 식별합니다. <br className="hidden sm:block" />
                                    재방문 사용자도 놓치지 않는 <strong>지속적인 여정 추적</strong>으로
                                    정확한 퍼널 데이터를 확보하세요.
                                </p>
                            </div>

                            <div className="absolute right-0 bottom-0 opacity-10">
                                <svg width="200" height="100" viewBox="0 0 200 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 80 C 50 80, 50 20, 100 20 C 150 20, 150 50, 200 50" stroke="currentColor" strokeWidth="4" className="text-purple-500" />
                                </svg>
                            </div>
                        </div>

                        {/* 2. 자동 리포트 */}
                        <div className="bg-gray-900 text-white rounded-2xl p-8 border border-gray-800 relative overflow-hidden group">
                            <div className="relative z-10 h-full flex flex-col justify-between">
                                <div>
                                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                                        <FileBarChart className="w-6 h-6 text-violet-400" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">자동 리포트 생성</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        데이터가 쌓이면 자동으로<br />
                                        퍼널 리포트를 생성합니다.<br />
                                        <strong>Celery 기반 비동기 처리</strong>로<br />
                                        서비스에 영향 없이 분석합니다.
                                    </p>
                                </div>
                                <div className="mt-6 pt-6 border-t border-gray-700">
                                    <div className="flex justify-between items-center text-sm font-mono text-gray-400">
                                        <span>Report Gen</span>
                                        <span className="text-green-400 font-bold">Auto</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 3. 초경량 SDK */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-300 group">
                            <div className="flex justify-between items-start mb-6">
                                <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center text-violet-600">
                                    <Zap className="w-6 h-6" />
                                </div>
                                <div className="text-xs font-mono text-gray-400 bg-gray-50 px-2 py-1 rounded">~5kb</div>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">초경량 SDK</h3>
                            <p className="text-gray-600 text-sm">
                                기존 A/B 테스트 SDK에 통합된 퍼널 추적.<br />
                                추가 스크립트 설치 없이 즉시 사용할 수 있습니다.
                            </p>
                        </div>

                        {/* 4. 디바이스 분리 + 프라이버시 */}
                        <div className="md:col-span-2 bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl p-8 border border-purple-100 flex flex-col sm:flex-row items-center gap-6">
                            <div className="flex-shrink-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm text-purple-500">
                                <ShieldCheck className="w-8 h-8" />
                            </div>
                            <div className="text-center sm:text-left">
                                <h3 className="text-xl font-bold text-gray-900 mb-2">데이터 무결성 보장</h3>
                                <p className="text-gray-600 text-sm">
                                    퍼널 단계 변경 시 기존 데이터를 자동 초기화하여 정확한 분석을 보장합니다.<br className="hidden sm:block" />
                                    서버 사이드 변경 감지로 불필요한 데이터 리셋 없이 효율적으로 운영됩니다.
                                </p>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 9: 히트맵 + A/B 테스트 시너지
      ========================================== */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="absolute top-1/2 left-0 w-96 h-96 bg-purple-100/50 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/3"></div>
                <div className="absolute top-1/2 right-0 w-96 h-96 bg-violet-100/50 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">

                        {/* Left: 텍스트 */}
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 border border-purple-100 text-purple-600 text-sm font-bold mb-6">
                                <Zap className="w-4 h-4 fill-purple-600" />
                                <span>Full Loop</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                <span className="text-purple-500">퍼널</span>로 어디를 찾고,<br />
                                <span className="text-orange-500">히트맵</span>으로 경향을 보고,<br />
                                <span className="text-cyan-600">세션 녹화</span>로 장면을 확인하고,<br />
                                <span className="text-blue-600">A/B 테스트</span>로 해결하세요
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                퍼널 분석만으로는 '어디서' 이탈하는지만 알 수 있습니다.
                                SwitchUp의 통합 플랫폼에서 발견 → 경향 파악 → 장면 확인 → 검증까지
                                끊김 없이 진행하세요.
                            </p>

                            <div className="space-y-4">
                                {/* Step 1 */}
                                <div className="flex items-center gap-4 bg-purple-50/50 p-4 rounded-xl border border-purple-100">
                                    <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">1</div>
                                    <div>
                                        <div className="font-bold text-gray-900">병목 발견 (Funnel)</div>
                                        <div className="text-sm text-gray-500">"장바구니→결제에서 62% 이탈" → 위치 파악</div>
                                    </div>
                                </div>

                                <div className="pl-7">
                                    <ArrowDown className="w-5 h-5 text-gray-300" />
                                </div>

                                {/* Step 2 */}
                                <div className="flex items-center gap-4 bg-orange-50/50 p-4 rounded-xl border border-orange-100">
                                    <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold">2</div>
                                    <div>
                                        <div className="font-bold text-gray-900">경향 파악 (Heatmap)</div>
                                        <div className="text-sm text-gray-500">"결제 페이지에서 배송비 영역 반복 클릭" → 의심 지점</div>
                                    </div>
                                </div>

                                <div className="pl-7">
                                    <ArrowDown className="w-5 h-5 text-gray-300" />
                                </div>

                                {/* Step 3 - Session Replay */}
                                <div className="flex items-center gap-4 bg-cyan-50/50 p-4 rounded-xl border border-cyan-100">
                                    <div className="w-10 h-10 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center font-bold">3</div>
                                    <div>
                                        <div className="font-bold text-gray-900">장면 확인 (Session Replay)</div>
                                        <div className="text-sm text-gray-500">"배송비 안내가 접혀 있어 못 찾고 이탈" → 원인 확정</div>
                                    </div>
                                </div>

                                <div className="pl-7">
                                    <ArrowDown className="w-5 h-5 text-gray-300" />
                                </div>

                                {/* Step 3 */}
                                <div className="flex items-center gap-4 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">4</div>
                                    <div>
                                        <div className="font-bold text-gray-900">가설 검증 (A/B Test)</div>
                                        <div className="text-sm text-gray-500">"무료배송 조건 표시 vs 기존" → 성능 비교</div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex flex-wrap gap-4">
                                <Link to="/service/heatmap">
                                    <button className="flex items-center gap-2 text-orange-600 font-bold hover:text-orange-700 transition-colors group">
                                        히트맵 기능 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                                <Link to="/service/ab-test">
                                    <button className="flex items-center gap-2 text-blue-600 font-bold hover:text-blue-700 transition-colors group">
                                        A/B 테스트 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                                <Link to="/service/replay">
                                    <button className="flex items-center gap-2 text-cyan-600 font-bold hover:text-cyan-700 transition-colors group">
                                        세션 녹화 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                            </div>
                        </div>

                        {/* Right: 시각화 다이어그램 */}
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl transform -rotate-3 scale-105 opacity-60 blur-xl"></div>

                            <div className="relative bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-xl flex flex-col items-center justify-center h-full min-h-[480px]">
                                <div className="flex flex-col items-center gap-6 w-full mb-8">

                                    {/* 1. Funnel */}
                                    <div className="text-center group">
                                        <div className="w-24 h-24 bg-purple-50 border border-purple-200 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                                            <Filter className="w-12 h-12 text-purple-500 fill-purple-500" />
                                        </div>
                                        <div className="font-bold text-gray-900 text-lg">병목 발견</div>
                                        <div className="text-xs text-gray-500 mt-1 font-medium">퍼널 분석</div>
                                    </div>

                                    <ArrowDown className="w-6 h-6 text-gray-300" />

                                    {/* 2. Heatmap */}
                                    <div className="text-center group">
                                        <div className="w-24 h-24 bg-orange-50 border border-orange-200 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                                            <Flame className="w-12 h-12 text-orange-500 fill-orange-500" />
                                        </div>
                                        <div className="font-bold text-gray-900 text-lg">경향 파악</div>
                                        <div className="text-xs text-gray-500 mt-1 font-medium">히트맵 분석</div>
                                    </div>

                                    <ArrowDown className="w-6 h-6 text-gray-300" />

                                    {/* 3. Session Replay */}
                                    <div className="text-center group">
                                        <div className="w-24 h-24 bg-cyan-50 border border-cyan-200 rounded-2xl flex items-center justify-center text-cyan-500 mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                                            <Video className="w-12 h-12 text-cyan-500 fill-cyan-500" />
                                        </div>
                                        <div className="font-bold text-gray-900 text-lg">장면 확인</div>
                                        <div className="text-xs text-gray-500 mt-1 font-medium">세션 녹화</div>
                                    </div>

                                    <ArrowDown className="w-6 h-6 text-gray-300" />

                                    {/* 3. A/B Test */}
                                    <div className="text-center group">
                                        <div className="w-24 h-24 bg-blue-50 border border-blue-200 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                                            <Split className="w-12 h-12 text-blue-500" />
                                        </div>
                                        <div className="font-bold text-gray-900 text-lg">가설 검증</div>
                                        <div className="text-xs text-gray-500 mt-1 font-medium">A/B 테스트</div>
                                    </div>
                                </div>

                                <div className="bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg animate-bounce flex items-center gap-2 z-10">
                                    <span>🚀</span>
                                    <span>비즈니스 성장 달성!</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 하단 연동 카드 */}
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-purple-50 to-violet-50/30 p-8 rounded-2xl border border-purple-100 hover:shadow-lg transition-all group">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-purple-600 shadow-sm group-hover:scale-110 transition-transform">
                                <Layers className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">퍼널 + 히트맵 연동 분석</h3>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                                퍼널에서 이탈률이 높은 페이지를 발견하면, 해당 페이지의 히트맵을 즉시 확인하세요.
                                클릭맵과 스크롤맵으로 이탈 원인을 시각적으로 파악할 수 있습니다.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-orange-50 to-red-50/30 p-8 rounded-2xl border border-orange-100 hover:shadow-lg transition-all group">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-orange-600 shadow-sm group-hover:scale-110 transition-transform">
                                <GitBranch className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">A/B 테스트 전후 퍼널 비교</h3>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                                A/B 테스트 전후의 퍼널 전환율을 비교하여 개선 효과를 정량적으로 측정하세요.
                                데이터에 기반한 의사결정으로 지속적인 전환율 최적화가 가능합니다.
                            </p>
                        </div>
                    </div>

                </div>
            </section>

            {/* ==========================================
          섹션 10: CTA Section
      ========================================== */}
            <section className="relative py-32 overflow-hidden bg-slate-900">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/40 via-slate-900 to-slate-900"></div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-purple-600/20 rounded-full blur-[120px] pointer-events-none"></div>
                <div className="absolute top-20 left-20 w-1 h-1 bg-white rounded-full animate-pulse"></div>
                <div className="absolute top-40 right-40 w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse delay-700"></div>

                <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm font-medium mb-8">
                        <Filter className="w-4 h-4 fill-purple-500" />
                        <span>Find the gap</span>
                    </div>

                    <h2 className="text-3xl md:text-6xl font-bold text-white mb-8 tracking-tight leading-tight">
                        고객 여정의 빈틈을 <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-500">지금 바로 찾으세요.</span>
                    </h2>

                    <p className="text-lg md:text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed">
                        97%의 이탈은 '이유'가 있습니다.<br className="hidden md:block" />
                        퍼널 분석으로 그 이유를 찾고, <br className="sm:hidden" />전환율을 극대화하세요.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/freestart" className="w-full sm:w-auto">
                            <button className="w-full px-8 py-4 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-400 hover:to-violet-500 text-white rounded-xl font-bold text-lg shadow-[0_0_40px_-10px_rgba(99,102,241,0.3)] hover:shadow-[0_0_60px_-15px_rgba(99,102,241,0.5)] hover:-translate-y-1 transition-all flex items-center justify-center gap-2 group">
                                무료로 시작하기
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </Link>
                        <Link to="/contact" className="w-full sm:w-auto">
                            <button className="w-full px-8 py-4 bg-transparent border border-slate-700 text-slate-300 rounded-xl font-medium hover:bg-slate-800 hover:text-white transition-all">
                                도입 문의하기
                            </button>
                        </Link>
                    </div>

                    <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-slate-500 font-medium">
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-purple-500" /> 30일 무료 체험
                        </div>
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-purple-500" /> 신용카드 불필요
                        </div>
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-purple-500" /> 언제든 취소 가능
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ServiceFunnel;